import React from "react";
import {
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import type { CanvasDataSource } from "../../../types";
import type { RelatedRecordsConfig } from "../elements/CanvasRelatedRecordsRenderer";
interface Props {
  props: Record<string, unknown>;
  dataSources: CanvasDataSource[];
  onChange: (props: Record<string, unknown>) => void;
}
/** The relationship is an existing View join/relation path; these are mappings, not a new relation definition. */
export function RelatedRecordsProperties({
  props,
  dataSources,
  onChange,
}: Props) {
  const config = (props.related_records_config || {}) as RelatedRecordsConfig;
  const patch = (key: string, value: unknown) =>
    onChange({ related_records_config: { ...config, [key]: value } });
  const sourceId = (alias: string) =>
    dataSources.find((source) =>
      [source.id, source.name, (source as { alias?: string }).alias].includes(
        alias,
      ),
    )?.id || null;
  const navigation = props.row_click_action as
    { config?: { target_canvas_id?: string } } | undefined;
  return (
    <Stack spacing={1.5}>
      <Divider />
      <Typography level="title-sm">Related records</Typography>
      <FormControl>
        <FormLabel>Source View</FormLabel>
        <Select
          value={sourceId(String(props.data_source_alias || ""))}
          onChange={(_, value) => onChange({ data_source_alias: value || "" })}
        >
          {dataSources
            .filter((s) => s.view_id)
            .map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Related View</FormLabel>
        <Select
          value={sourceId(config.related_data_source_alias || "")}
          onChange={(_, value) =>
            onChange({
              related_records_config: {
                ...config,
                related_data_source_alias: value || "",
                display_fields: [],
              },
            })
          }
        >
          {dataSources
            .filter((s) => s.view_id)
            .map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Relationship path</FormLabel>
        <Input
          value={config.relationship_path || ""}
          onChange={(e) => patch("relationship_path", e.target.value)}
        />
        <Typography level="body-xs">
          Existing relation field or joined-table alias; nested paths use dots.
        </Typography>
      </FormControl>
      <FormControl>
        <FormLabel>Display fields (comma separated)</FormLabel>
        <Input
          value={(config.display_fields || []).join(", ")}
          onChange={(e) =>
            patch(
              "display_fields",
              e.target.value
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean),
            )
          }
        />
      </FormControl>
      <FormControl>
        <FormLabel>Records per page</FormLabel>
        <Input
          type="number"
          slotProps={{ input: { min: 1, max: 100 } }}
          value={config.max_records ?? 10}
          onChange={(e) => patch("max_records", Number(e.target.value))}
        />
      </FormControl>
      <Checkbox
        label="Allow editing related records"
        checked={config.allow_edit === true}
        onChange={(e) => patch("allow_edit", e.target.checked)}
      />
      <Checkbox
        label="Allow creating related records"
        checked={config.allow_create === true}
        onChange={(e) => patch("allow_create", e.target.checked)}
      />
      <Typography level="body-xs">
        View permissions and the canvas path still apply. Delete is not enabled.
      </Typography>
      <FormControl>
        <FormLabel>Detail canvas ID (optional)</FormLabel>
        <Input
          value={navigation?.config?.target_canvas_id || ""}
          onChange={(e) =>
            onChange({
              row_click_action: e.target.value
                ? {
                    type: "NAVIGATE",
                    config: {
                      target_canvas_id: e.target.value,
                      record_source: "selected",
                      return_to_source: true,
                    },
                  }
                : undefined,
            })
          }
        />
      </FormControl>
    </Stack>
  );
}
